import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { agrogestao } from '@/api/agrogestaoClient';
import {
    Building2, Plus, Wallet, ArrowUpRight, ArrowDownLeft,
    MoreVertical, CreditCard, History, Trash2, Edit2, Check, X
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { motion, AnimatePresence } from "framer-motion";
import { format } from 'date-fns';

export default function ControleBancario() {
    const propriedadeId = localStorage.getItem("propriedade_selecionada");
    const queryClient = useQueryClient();
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isStatementOpen, setIsStatementOpen] = useState(false);

    // Form State
    const [formData, setFormData] = useState({
        id: null,
        banco: '',
        agencia: '',
        numero: '',
        tipo: 'corrente',
        saldo_inicial: '0',
        descricao: ''
    });

    const { data: contas = [], isLoading } = useQuery({
        queryKey: ['contas_bancarias', propriedadeId],
        queryFn: () => propriedadeId
            ? agrogestao.entities.ContaBancaria.filter({ propriedade_id: propriedadeId })
            : Promise.resolve([]),
        enabled: !!propriedadeId
    });

    const saveMutation = useMutation({
        mutationFn: async (data) => {
            const payload = {
                propriedade_id: propriedadeId,
                banco: data.banco,
                agencia: data.agencia,
                numero: data.numero,
                tipo: data.tipo,
                saldo_inicial: parseFloat(data.saldo_inicial),
                // If creating, saldo_atual = saldo_inicial. If updating, usually we don't change saldo_atual explicitly here unless it's a correction
                // For simplicity in this "setup" phase, we'll set saldo_atual = saldo_inicial on create
                ...(data.id ? {} : { saldo_atual: parseFloat(data.saldo_inicial) }),
                descricao: data.descricao
            };

            if (data.id) {
                return agrogestao.entities.ContaBancaria.update(data.id, payload);
            }
            return agrogestao.entities.ContaBancaria.create(payload);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['contas_bancarias'] });
            setIsDialogOpen(false);
            resetForm();
        }
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => agrogestao.entities.ContaBancaria.delete(id),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['contas_bancarias'] })
    });

    const resetForm = () => {
        setFormData({
            id: null,
            banco: '',
            agencia: '',
            numero: '',
            tipo: 'corrente',
            saldo_inicial: '0',
            descricao: ''
        });
    };

    const handleEdit = (conta) => {
        setFormData({
            id: conta.id,
            banco: conta.banco,
            agencia: conta.agencia,
            numero: conta.numero,
            tipo: conta.tipo || 'corrente',
            saldo_inicial: conta.saldo_inicial,
            descricao: conta.descricao || ''
        });
        setIsDialogOpen(true);
    };

    const formatCurrency = (val) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);

    const totalSaldo = contas.reduce((acc, c) => acc + (c.saldo_atual || 0), 0);

    return (
        <div className="space-y-6">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
                        <Building2 className="w-6 h-6 text-emerald-600" />
                        Controle Bancário
                    </h2>
                    <p className="text-stone-600">Gerencie contas, saldos e conciliações</p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="px-4 py-2 bg-emerald-50 rounded-lg border border-emerald-100 flex flex-col items-end">
                        <span className="text-xs text-emerald-600 uppercase font-semibold">Saldo Total</span>
                        <span className="text-xl font-bold text-emerald-800">{formatCurrency(totalSaldo)}</span>
                    </div>
                    <Button onClick={() => { resetForm(); setIsDialogOpen(true); }} className="bg-stone-900 hover:bg-stone-800 text-white gap-2 shadow-lg hover:shadow-xl transition-all">
                        <Plus className="w-4 h-4" />
                        Nova Conta
                    </Button>
                </div>
            </div>

            {/* Grid of Accounts */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <AnimatePresence>
                    {contas.map((conta, index) => (
                        <motion.div
                            key={conta.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ delay: index * 0.05 }}
                        >
                            <Card className="group relative overflow-hidden border-stone-200 hover:border-emerald-500/50 transition-all duration-300 hover:shadow-lg">
                                {/* Abstract Decoration */}
                                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-stone-50 to-transparent -mr-8 -mt-8 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />

                                <CardHeader className="pb-2 relative z-10">
                                    <div className="flex justify-between items-start">
                                        <div className="space-y-1">
                                            <CardTitle className="text-lg font-bold text-stone-800 flex items-center gap-2">
                                                {conta.banco}
                                            </CardTitle>
                                            <CardDescription className="text-xs font-mono bg-stone-100 px-2 py-1 rounded w-fit">
                                                AG: {conta.agencia} • CC: {conta.numero}
                                            </CardDescription>
                                        </div>
                                        <div className="flex gap-1">
                                            <Button variant="ghost" size="icon" className="h-8 w-8 text-stone-400 hover:text-stone-700" onClick={() => handleEdit(conta)}>
                                                <Edit2 className="w-4 h-4" />
                                            </Button>
                                            <Button variant="ghost" size="icon" className="h-8 w-8 text-stone-400 hover:text-red-600" onClick={() => deleteMutation.mutate(conta.id)}>
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </CardHeader>

                                <CardContent className="relative z-10">
                                    <div className="mt-4">
                                        <p className="text-sm text-stone-500 mb-1">Saldo Atual</p>
                                        <div className="flex items-baseline gap-2">
                                            <span className={`text-2xl font-bold ${conta.saldo_atual < 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                                                {formatCurrency(conta.saldo_atual)}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="mt-6 pt-4 border-t border-stone-100 flex gap-2">
                                        <Button variant="secondary" className="w-full text-xs font-medium" onClick={() => { setSelectedAccount(conta); setIsStatementOpen(true); }}>
                                            <History className="w-3 h-3 mr-2" />
                                            Extrato
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))}

                    {/* Empty State */}
                    {contas.length === 0 && !isLoading && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="col-span-full py-12 flex flex-col items-center justify-center text-center bg-stone-50 rounded-2xl border border-dashed border-stone-300"
                        >
                            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm mb-4">
                                <Wallet className="w-8 h-8 text-stone-300" />
                            </div>
                            <h3 className="text-lg font-semibold text-stone-700">Nenhuma conta cadastrada</h3>
                            <p className="text-stone-500 max-w-sm mt-1 mb-6">Cadastre suas contas bancárias para controlar saldos e conciliar pagamentos automaticamente.</p>
                            <Button onClick={() => { resetForm(); setIsDialogOpen(true); }} className="bg-emerald-600 hover:bg-emerald-700">
                                Cadastrar Primeira Conta
                            </Button>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Account Dialog */}
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>{formData.id ? 'Editar Conta' : 'Nova Conta Bancária'}</DialogTitle>
                        <DialogDescription>Preencha os dados da conta para controle financeiro.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Banco / Instituição</Label>
                                <Input placeholder="Ex: Banco do Brasil" value={formData.banco} onChange={e => setFormData({ ...formData, banco: e.target.value })} />
                            </div>
                            <div className="space-y-2">
                                <Label>Tipo</Label>
                                <Select value={formData.tipo} onValueChange={v => setFormData({ ...formData, tipo: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="corrente">Conta Corrente</SelectItem>
                                        <SelectItem value="poupanca">Poupança</SelectItem>
                                        <SelectItem value="investimento">Investimento</SelectItem>
                                        <SelectItem value="caixa">Caixa Físico</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Agência</Label>
                                <Input placeholder="0000" value={formData.agencia} onChange={e => setFormData({ ...formData, agencia: e.target.value })} />
                            </div>
                            <div className="space-y-2">
                                <Label>Número da Conta</Label>
                                <Input placeholder="00000-0" value={formData.numero} onChange={e => setFormData({ ...formData, numero: e.target.value })} />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Saldo Inicial (R$)</Label>
                            <Input
                                type="number"
                                step="0.01"
                                value={formData.saldo_inicial}
                                onChange={e => setFormData({ ...formData, saldo_inicial: e.target.value })}
                                disabled={!!formData.id} // Disable editing initial balance after creation to ensure integrity
                                title={formData.id ? "Saldo inicial não pode ser alterado" : ""}
                            />
                            {formData.id && <p className="text-xs text-stone-500">Para ajustar o saldo, faça um lançamento de ajuste.</p>}
                        </div>
                        <div className="space-y-2">
                            <Label>Descrição / Apelido</Label>
                            <Input placeholder="Ex: Conta Principal Safra" value={formData.descricao} onChange={e => setFormData({ ...formData, descricao: e.target.value })} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
                        <Button onClick={() => saveMutation.mutate(formData)} className="bg-stone-900 text-white hover:bg-stone-800" disabled={!formData.banco}>
                            {saveMutation.isPending && <CreditCard className="w-4 h-4 mr-2 animate-spin" />}
                            Salvar Conta
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Statement Sheet/Dialog */}
            <Dialog open={isStatementOpen} onOpenChange={setIsStatementOpen}>
                <DialogContent className="sm:max-w-3xl h-[80vh] flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <History className="w-5 h-5 text-emerald-600" />
                            Extrato Bancário
                        </DialogTitle>
                        <DialogDescription>
                            {selectedAccount?.banco} - Ag: {selectedAccount?.agencia} CC: {selectedAccount?.numero}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex-1 overflow-y-auto min-h-0 pr-2">
                        {selectedAccount && <ExtratoList contaId={selectedAccount.id} />}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function ExtratoList({ contaId }) {
    const propriedadeId = localStorage.getItem("propriedade_selecionada");

    const { data: extrato = [], isLoading } = useQuery({
        queryKey: ['extrato_bancario', contaId],
        queryFn: () => agrogestao.entities.ExtratoBancario.filter({ conta_bancaria_id: contaId }, '-data'),
        enabled: !!contaId
    });

    const formatCurrency = (val) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);

    if (isLoading) return <div className="py-10 text-center text-stone-500">Carregando movimentações...</div>;

    if (extrato.length === 0) return (
        <div className="py-20 text-center flex flex-col items-center text-stone-400">
            <History className="w-12 h-12 mb-3 opacity-20" />
            <p>Nenhuma movimentação registrada.</p>
        </div>
    );

    return (
        <div className="space-y-4">
            <div className="divide-y divide-stone-100">
                {extrato.map((item) => (
                    <div key={item.id} className="py-3 flex items-center justify-between hover:bg-stone-50 px-2 rounded-lg transition-colors">
                        <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${item.tipo === 'credito' ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'}`}>
                                {item.tipo === 'credito' ? <ArrowUpRight className="w-5 h-5" /> : <ArrowDownLeft className="w-5 h-5" />}
                            </div>
                            <div>
                                <p className="font-medium text-stone-800">{item.descricao}</p>
                                <p className="text-xs text-stone-500">{format(new Date(item.data), "dd/MM/yyyy HH:mm")}</p>
                            </div>
                        </div>
                        <div className="text-right">
                            <p className={`font-bold ${item.tipo === 'credito' ? 'text-emerald-700' : 'text-red-700'}`}>
                                {item.tipo === 'credito' ? '+' : '-'}{formatCurrency(item.valor)}
                            </p>
                            <Badge variant="outline" className="text-[10px] text-stone-400 border-stone-200">
                                {item.origem_tipo || 'Manual'}
                            </Badge>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
